import React, { useMemo, useState } from 'react';
import styled, { css } from 'styled-components/macro';
import {
  Layout, Error,
} from 'components/organisms';
import {
  Title, HelperTitle, Text, Spacer, Button, Seperator
} from 'components/atoms';
import { TabsLineList, Tooltip } from 'components/molecules';
import {
  useParams, Link, useLocation, useHistory
} from 'react-router-dom';
import { toast } from 'react-toastify';
import { MenuEditor, SubwebsPagesTableSelect, Toast } from 'containers'
import { useUserContext } from 'context/UserContext';
import { useSelector, useDispatch } from 'react-redux'
import { generateItem, addItemsToTree } from 'modules/SideNav/parser/navToTree'
import { updateTree } from 'redux/sidenavSlice'
import queryString from 'query-string';
import SideNavEditorLinkForm from 'modules/SideNav/SideNavEditorLinkForm'
import SideNavEditorParentForm from 'modules/SideNav/SideNavEditorParentForm'
import { generateKey, generateSubwebPath } from 'services/generators'
import { injectIntl } from 'react-intl'
import { intlType } from 'types'
import Select from 'react-select'

const options = [
  {
    label: 'Edição em PT',
    value: 'pt',
  },
  {
    label: 'Edição em EN',
    value: 'en',
  },
];

const Page = ({ intl }) => {
  const { subweb, lang } = useParams()
  const { search } = useLocation()
  const history = useHistory()
  const [{ user }] = useUserContext();
  const dispatch = useDispatch()
  const { tree } = useSelector(
    (state) => state.sidenav
  )
  const [link, setLink] = useState({
    url: '',
    title: ''
  })
  const [parentItem, setParentItem] = useState({
    title: ''
  })

  // state used to trigger an action on child editor component
  // everytime this changes the child editor knows it should handle the add
  const [publishTrigger, setPublishTrigger] = useState(false);

  const subwebData = useMemo(() => {
    if (!user || !user.subwebs || !user.subwebs.length > 0) return null
    const data = user.subwebs.find(
      subwebData => subwebData.slug === subweb || encodeURIComponent(subwebData.slug) === subweb
    )
    return data
  }, [subweb, user])

  const error = useMemo(() => {
    if (!subweb) {
      return {
        message: 'Subweb não encontrada',
        code: 404
      }
    }
    return {
      message: `Sem permissões para editar a subweb: ${subweb}`,
      code: 401
    }
  }, [subweb])

  const handleAddPage = (page) => {
    const { id, slug, title } = page
    // add page to tree
    const treeWithPage = addItemsToTree(
      tree,
      {
        ...generateItem({ id, slug, title })
      }
    )
    dispatch(updateTree(treeWithPage))
    toast.success(
      <Toast
        type="success"
        title={`${title} associada ao menu`}
        description="Para gravar as alterações clique em publicar"
      />
    );
  }

  const handleAddLink = () => {
    const treeWithPage = addItemsToTree(
      tree,
      {
        ...generateItem({ id: generateKey(link.title), slug: link.url, title: link.title })
      }
    )
    dispatch(updateTree(treeWithPage))
    toast.success(
      <Toast
        type="success"
        title="Link associado ao menu"
        description="Para gravar as alterações clique em publicar"
      />
    );
    setLink({ url: '', title: '' })
  }

  const handleAddParent = () => {
    const newItem = generateItem({ id: generateKey(parentItem.title), slug: '', title: parentItem.title })
    const treeWithPage = addItemsToTree(
      tree,
      newItem
    )
    dispatch(updateTree(treeWithPage))
    toast.success(
      <Toast
        type="success"
        title="Item associado ao menu"
        description="Para gravar as alterações clique em publicar"
      />
    );
    setParentItem({ title: '' })
  }

  const queryParams = useMemo(() => queryString.parse(search), [search])

  const add = useMemo(() => ({
    isAddingPage: !('add' in queryParams) || queryParams.add === 'page',
    isAddingLink: queryParams && queryParams.add && queryParams.add === 'link',
    isAddingParent: queryParams && queryParams.add && queryParams.add === 'parent'
  }), [queryParams])

  const tabs = useMemo(() => [
    {
      to: `/${subweb}/editor/${lang}/nav?add=page`,
      text: 'associar página',
      isActive: add.isAddingPage
    },
    {
      to: `/${subweb}/editor/${lang}/nav?add=link`,
      text: 'criar link',
      isActive: add.isAddingLink
    },
    {
      to: `/${subweb}/editor/${lang}/nav?add=parent`,
      text: 'criar item',
      isActive: add.isAddingParent
    }
  ], [subweb, lang, add])

  const subwebUrl = useMemo(
    () => generateSubwebPath({ subweb, withDomain: true, withRootHome: true }),
    [subweb])

  const activeLangValue = useMemo(() => {
    const activeValue = options.find(values => values.value === lang);
    return activeValue;
  }, [lang]);

  const changeLanguage = ({ value: newLang }) => {
    const pre = `/${subweb}/editor/${newLang}/nav`
    const query = queryString.stringify(queryParams)
    const newPath = pre + (query === '' ? '' : `?${query}`)
    history.push(newPath);
  }

  if (!subwebData) {
    return (
      <Layout modifiers={['center']}>
        <Error
          message={error.message}
          code={error.code}
          helper={(
            <Text>
              <Link to="/">Voltar à página inicial</Link>
            </Text>
          )}
        />
      </Layout>
    )
  }

  return (
    <Layout modifiers={['stretch']}>
      <SideBySide>
        <SideBySide.Start>
          <Top>
            <Link to={`/${subweb}`}>
              <Title>{subwebData.description}</Title>
            </Link>
            <Text modifiers={['light']}>navegação</Text>
          </Top>
          <Spacer bottom="xl2">
            <HelperTitle>
            menu de navegação
            (subweb)
            </HelperTitle>
            <MenuEditor triggerPublish={publishTrigger} />
          </Spacer>
        </SideBySide.Start>
        <SideBySide.End>
          <Top.End>
            <GlobalActions>
              <Select
                classNamePrefix="react-select"
                noOptionsMessage={() => 'Sem opções'}
                options={options}
                name="language"
                value={activeLangValue}
                onChange={changeLanguage}
                isSearchable={false}
              />
              <Spacer right="xs2" />
              <Button
                modifiers={['primary']}
                onClick={() => setPublishTrigger(!publishTrigger)}
              >
              publicar
              </Button>
              <Spacer right="xs2" />
              <Tooltip tooltip="visitar subweb no portal" placement="bottom">
                <a href={subwebUrl} target="_blank" rel="noopener noreferrer">
                  <Button>
                    {intl.formatMessage({ id: 'subwebs.visit' })}
                  </Button>
                </a>
              </Tooltip>
            </GlobalActions>
          </Top.End>
          <Spacer bottom="xl2">
            <HelperTitle>
              outros itens
            </HelperTitle>
            <TabsWrapper>
              <TabsLineList tabs={tabs} modifiers={['stretch', 'primary']} />
            </TabsWrapper>
            <Seperator />
            <Spacer bottom="m" />
            {add.isAddingPage && (
              <SubwebsPagesTableSelect
                subweb={subweb}
                lang={lang}
                onAddPage={handleAddPage}
              />
            )}
            {add.isAddingLink && (
              <FormWrapper>
                <SideNavEditorLinkForm
                  initialValues={link}
                  onChange={setLink}
                  onSubmit={handleAddLink}
                />
              </FormWrapper>
            )}
            {add.isAddingParent && (
              <FormWrapper>
                <SideNavEditorParentForm
                  initialValues={parentItem}
                  onChange={setParentItem}
                  onSubmit={handleAddParent}
                />
              </FormWrapper>
            )}
          </Spacer>
        </SideBySide.End>
      </SideBySide>
    </Layout>
  );
}

const SideBySide = styled.div`
  display: flex;

  ${HelperTitle} {
    margin-bottom: ${props => props.theme.spacing.l};
  }
  ${props => props.theme.mixins.select()};
  .react-select__control {
    background-color: ${props => props.theme.colors.bgLighterS} !important;
    padding: ${props => `${props.theme.spacing.xs} ${props.theme.spacing.sm}`} !important;
    min-width: 150px !important;
  }
  .react-select__option {
    text-align: center;
  }
`;

SideBySide.Start = styled.div`
  background-color: ${props => props.theme.colors.bgLighterS};
  padding: ${props => `
  ${props.theme.components.layout.paddingVertical}
  ${' '}
  ${props.theme.components.layout.paddingHorizontal}
  `};
  border-right: 2px solid ${props => props.theme.colors.borderLighter};
`

SideBySide.End = styled.div`
  width: 100%;
  padding: ${props => `
  ${props.theme.components.layout.paddingVertical}
  ${' '}
  ${props.theme.components.layout.paddingHorizontal}
  `};
`

const top = css`
  margin-bottom: ${props => props.theme.spacing.l};
  padding-bottom: ${props => props.theme.spacing.xl4};
  display: inline-flex;
  width: 100%;
`

const Top = styled.div`
  ${top};
  flex-direction: column;
  a {
    text-decoration: none;
    &:hover {
      text-decoration: underline;
    }
  }
`

Top.End = styled.div`
  ${top};
  justify-content: flex-end;
  height: 121px;
  a {
    text-decoration: none !important;
    font-size: ${props => props.theme.sizes.m} !important;
  }
`

const GlobalActions = styled.div`
  display: flex;
  height: fit-content;
  a {
    text-decoration: none !important;
    font-size: ${props => props.theme.sizes.m} !important;
  }
`

SideBySide.Header = styled.div`
  margin-bottom: ${props => props.theme.spacing.l};
`;

const TabsWrapper = styled.div`
  transform: translateY(2px);
`

const FormWrapper = styled.div`
  max-width: 400px;
`

Page.propTypes = {
  intl: intlType.isRequired,
}

export default injectIntl(Page);
